/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The desktop rail ask for the declared product on a best-list article — the PDP hero rail's
 * ask (`OwnerRailAsk`), in the sidebar, with the same words. Plan:
 * docs/owner-notes-guide-funnel-plan.md (WP2).
 *
 * Renders nothing at all unless the reader has declared a product AND publishing could succeed
 * (`turnstileConfigured`) AND the capture block says an ask is what to show (`railAskMode` over
 * the own-note signal `GuideCapture` publishes): an author is never asked again, and nothing
 * shows while `/me` is pending. The click never hosts the form — it asks the capture block to
 * open the band under its strip (`requestOpenOwnerBand`).
 */
import { useEffect, useMemo, useRef } from 'react'
import styles from './GuideRailAsk.module.css'
import { useOnScreenOnce, useOwnerTrack } from 'lib/owner-notes/analytics'
import {
  ASK_COPY,
  BAND_CTA,
  DRAFT_WAITING_CTA,
  GATED_ASK_COPY,
  ownersFromCard,
  railAskMode,
} from 'lib/owner-notes/band-copy'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import { useBandDraftWaiting } from 'lib/owner-notes/draft-waiting'
import { requestOpenOwnerBand, useOwnNoteSignal, useSeatedSurface } from 'lib/owner-notes/bus'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import HandoffHint from 'components/owner-notes/HandoffHint'
import Linked from 'shared-components/service/Linked'
import { useDeclaredContext } from 'lib/owner-notes/declared'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import type { DSNCOwnerGuideRail } from 'types.sg.ds'

interface GuideRailAskProps {
  data: DSNCOwnerGuideRail
}

const GuideRailAsk: React.VFC<GuideRailAskProps> = ({ data }) => {
  const declared = useDeclaredContext()?.declared ?? null
  const product = useMemo(() => {
    if (!declared?.nodeId) {
      return null
    }
    return { nodeId: declared.nodeId, name: declared.name }
  }, [declared?.nodeId, declared?.name])
  const trackOwner = useOwnerTrack(product)
  // One Review Writer (P1): the rail links the writer for the declared product.
  const handoff = useWriterHandoff('rail', declared?.href, { flag: 'guide' })
  const enabled = turnstileConfigured()
  const signal = useOwnNoteSignal()
  const mode = railAskMode(signal)
  // One ask per guide (2026-10-17): while a pick's block seats it, the rail says nothing.
  const seated = useSeatedSurface()
  // Not while the band is open under the strip: the reader is filling the form in: a rail
  // inviting them to start it is the second invite `bandOpen` exists to stop.
  const visible = Boolean(product) && enabled && mode === 'ask' && !signal.bandOpen && !seated
  // The sign-in gate (slice D): the ask says it, inline or handed off; an inline ask whose band
  // draft is waiting in this tab says "Finish your owner review" (client-only, after mount).
  const gated = signInGateFor('guide')
  const draftWaiting = useBandDraftWaiting(
    product?.nodeId ?? '',
    Boolean(product) && !handoff && gated,
    signal.bandOpen
  )

  // Owner counts only where they are already in hand: a declared product that IS one of this
  // guide's picks carries DealHunt's build-time counts (#110 alias match). For a product
  // declared elsewhere the rail says what the form costs instead of a count — never a resolve
  // to fill a line (Directive cae90548), and never "be the first" about a product we have not
  // counted.
  const owners = useMemo(() => {
    const nodeId = product?.nodeId ?? null
    if (!nodeId) {
      return null
    }
    const pick = data.picks.find(
      p =>
        p.context !== null && (p.context.node_id === nodeId || p.context.node_ids.includes(nodeId))
    )
    return pick?.context ? ownersFromCard(pick.context.owners) : null
  }, [data.picks, product?.nodeId])

  const askRef = useRef<HTMLDivElement>(null)
  const seen = useOnScreenOnce(askRef, visible)
  const shown = useRef(false)
  useEffect(() => {
    if (visible && seen && !shown.current) {
      shown.current = true
      trackOwner('ask_shown', { location: 'rail' })
    }
  }, [visible, seen, trackOwner])

  if (!visible || !product) {
    return null
  }
  return (
    <div className={styles.ask} ref={askRef}>
      <p className={styles.title}>{ASK_COPY.title(product.name)}</p>
      <p className={styles.text}>
        {owners
          ? (gated ? GATED_ASK_COPY : ASK_COPY).rail(owners.count)
          : (gated ? GATED_ASK_COPY : ASK_COPY).section}
      </p>
      {handoff ? (
        <>
          <Linked href={handoff.href()} className={styles.cta} {...handoff.linkProps}>
            {BAND_CTA}
          </Linked>
          <HandoffHint text={handoff.hint} />
        </>
      ) : (
        <button type="button" className={styles.cta} onClick={() => requestOpenOwnerBand('rail')}>
          {draftWaiting ? DRAFT_WAITING_CTA : BAND_CTA}
        </button>
      )}
    </div>
  )
}

export default GuideRailAsk
